import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  Paper,
  Button,
  IconButton,
  Chip,
  Collapse,
  CircularProgress,
} from "@mui/material";
import { Print, FilterAltOff, ExpandMore, InfoOutlined, WarningAmber } from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FaqButton } from "../components/FaqButton";
import { FooterInternal } from "../components/Footer";
import MonthSelect from "../components/Inputs/MonthSelect";
import { useRouter } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import {
  occupancyReportService,
  OccupancyResponse,
  OccupancyHeatCell,
} from "../services/reportsService";
import { colors, typography, pageHeader } from "../theme/designSystem";
import { filterFieldSx } from "../theme/filterStyles";

// Ocupação de Salas — horas agendadas x capacidade, por sala, turno e dia da semana.
// A sala do atendimento é a sala padrão do profissional; remanejamento pontual não é
// registrado enquanto a agenda estiver no Bubble.

const OCCUPANCY_LOW = 50;
const OCCUPANCY_HIGH = 75;

// abaixo de 50% = alerta; 50-75% = atenção; acima de 75% = saudável
const occupancyColor = (percent: number): string => {
  if (percent < OCCUPANCY_LOW) return "#dc3545";
  if (percent <= OCCUPANCY_HIGH) return "#ffc107";
  return "#28a745";
};

const formatPercent = (value: number): string =>
  `${value.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`;

const formatHours = (value: number): string =>
  `${value.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}h`;

// o backend pode devolver os rótulos em inglês; exibe sempre em PT-BR
const WEEKDAY_LABELS: Record<string, string> = {
  monday: "Segunda",
  tuesday: "Terça",
  wednesday: "Quarta",
  thursday: "Quinta",
  friday: "Sexta",
  saturday: "Sábado",
  sunday: "Domingo",
};

const SHIFT_LABELS: Record<string, string> = {
  morning: "Manhã",
  afternoon: "Tarde",
  evening: "Noite",
  night: "Noite",
};

const label = (value: string, dictionary: Record<string, string>): string =>
  dictionary[value.trim().toLowerCase()] || value;

// os dias indisponíveis já vêm traduzidos do backend ("Segunda", "Quarta")
const rentalLabel = (weekdays: string[], fullyRented: boolean): string => {
  if (fullyRented) return "Indisponível (todos os dias)";
  if (!weekdays || weekdays.length === 0) return "";
  return `Indisponível: ${weekdays.join(", ")}`;
};

const currentCompetence = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const competenceBounds = (competence: string): { from: string; to: string } => {
  const [year, month] = competence.split("-").map((v) => parseInt(v, 10));
  const lastDay = new Date(year, month, 0).getDate();
  const mm = String(month).padStart(2, "0");
  return { from: `${year}-${mm}-01`, to: `${year}-${mm}-${String(lastDay).padStart(2, "0")}` };
};


const filterCardSx = {
  backgroundColor: "white",
  borderRadius: "12px",
  padding: "1.5rem",
  boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
  border: "1px solid #e9ecef",
  mb: 3,
};

const actionButtonSx = {
  padding: "0.5rem 1rem",
  borderRadius: "4px",
  fontSize: "0.85rem",
  fontWeight: typography.fontWeight.semibold,
  textTransform: "none" as const,
  boxShadow: "none",
  backgroundColor: colors.primary,
  color: "#fff",
  "&:hover": { backgroundColor: colors.primaryHover, boxShadow: "none" },
  "&.Mui-disabled": { backgroundColor: "#e9ecef", color: "#adb5bd" },
};

const clearFilterButtonSx = {
  backgroundColor: "#6c757d",
  color: "white",
  width: "40px",
  height: "40px",
  borderRadius: "4px",
  transition: "all 0.2s ease",
  "&:hover": { backgroundColor: "#5a6268" },
  "&:disabled": { backgroundColor: "#e9ecef", color: "#adb5bd", opacity: 0.6 },
};

const sectionStyle = {
  background: "#fff",
  borderRadius: 2,
  border: "1px solid #e9ecef",
  p: 2,
  mb: 3,
};

const summaryCardSx = {
  flex: "1 1 190px",
  background: "#fff",
  borderRadius: 2,
  border: "1px solid #e9ecef",
  p: 2,
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  minHeight: 88,
};

/** Cabeçalho de bloco que abre/fecha o conteúdo (acordeão padrão do sistema). */
const SectionHeader: React.FC<{
  title: string;
  open: boolean;
  onToggle: () => void;
  subtitle?: string;
}> = ({ title, open, onToggle, subtitle }) => (
  <Box
    onClick={onToggle}
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      cursor: "pointer",
      userSelect: "none",
      mb: open ? 0.5 : 0,
    }}
  >
    <Box>
      <Typography sx={{ fontSize: 15, fontWeight: typography.fontWeight.semibold }}>
        {title}
      </Typography>
      {subtitle && open && (
        <Typography sx={{ fontSize: 12, color: colors.textSecondary, mt: 0.25 }}>
          {subtitle}
        </Typography>
      )}
    </Box>
    <IconButton size="small" aria-label={open ? "Recolher" : "Expandir"} className="no-print">
      <ExpandMore
        fontSize="small"
        sx={{ transition: "transform 200ms", transform: open ? "rotate(180deg)" : "none" }}
      />
    </IconButton>
  </Box>
);

/** Barra de ocupação com a cor do semáforo (vermelho/amarelo/verde). */
const OccupancyBar: React.FC<{ percent: number }> = ({ percent }) => (
  <Box sx={{ flex: 1, height: 8, background: "#f1f3f5", borderRadius: 4, overflow: "hidden", minWidth: 60 }}>
    <Box
      sx={{
        width: `${Math.min(100, Math.max(0, percent))}%`,
        height: "100%",
        background: occupancyColor(percent),
        borderRadius: 4,
      }}
    />
  </Box>
);

/**
 * Banda de ocupação: trecho sólido = horas com sala conhecida (piso);
 * trecho hachurado = horas sem sala definida, que elevam a ocupação até o teto.
 */
const OccupancyRangeBar: React.FC<{ floor: number; ceiling: number }> = ({ floor, ceiling }) => {
  const safeFloor = Math.min(100, Math.max(0, floor));
  const safeCeiling = Math.min(100, Math.max(safeFloor, ceiling));
  return (
    <Box
      sx={{
        display: "flex",
        height: 14,
        background: "#f1f3f5",
        borderRadius: 7,
        overflow: "hidden",
        mt: 1.5,
      }}
    >
      <Box sx={{ width: `${safeFloor}%`, background: occupancyColor(floor) }} />
      <Box
        sx={{
          width: `${safeCeiling - safeFloor}%`,
          backgroundImage: `repeating-linear-gradient(45deg, ${occupancyColor(
            floor,
          )}55, ${occupancyColor(floor)}55 5px, #f1f3f5 5px, #f1f3f5 10px)`,
        }}
      />
    </Box>
  );
};

// rótulo curto do dia da semana (heatmap usa weekday numérico: 1=seg … 6=sáb)
const WEEKDAY_SHORT: Record<number, string> = {
  1: "Seg",
  2: "Ter",
  3: "Qua",
  4: "Qui",
  5: "Sex",
  6: "Sáb",
};

const hourLabel = (hour: number): string => `${String(hour).padStart(2, "0")}h`;

// Escala sequencial de magnitude (não é semáforo): interpola branco (#ffffff) → teal do
// design system (#03B4C6) conforme a ocupação. 0% ≈ branco, 100% = teal cheio.
const HEAT_TEAL = { r: 3, g: 180, b: 198 };
const heatColor = (percent: number): string => {
  const t = Math.min(1, Math.max(0, percent / 100));
  const mix = (channel: number) => Math.round(255 + (channel - 255) * t);
  return `rgb(${mix(HEAT_TEAL.r)}, ${mix(HEAT_TEAL.g)}, ${mix(HEAT_TEAL.b)})`;
};

// acima de 55% o fundo teal fica escuro o bastante para exigir texto branco
const heatTextColor = (percent: number): string => (percent > 55 ? "#fff" : colors.textPrimary);

/** Mapa de calor dia da semana × hora — magnitude de ocupação por faixa. */
const OccupancyHeatmap: React.FC<{ cells: OccupancyHeatCell[] }> = ({ cells }) => {
  const hours = Array.from(new Set(cells.map((c) => c.hour))).sort((a, b) => a - b);
  const hasSaturday = cells.some((c) => c.weekday === 6);
  const weekdays = hasSaturday ? [1, 2, 3, 4, 5, 6] : [1, 2, 3, 4, 5];
  const cellByKey = new Map<string, OccupancyHeatCell>();
  cells.forEach((c) => cellByKey.set(`${c.weekday}-${c.hour}`, c));

  const stickyLabelSx = {
    position: "sticky" as const,
    left: 0,
    zIndex: 1,
    width: 56,
    background: "#fff",
    fontSize: 12,
    fontWeight: 600,
    color: colors.textSecondary,
    textAlign: "left" as const,
    padding: "0 8px",
    whiteSpace: "nowrap" as const,
  };

  return (
    <>
      <Box className="occupancy-scroll" sx={{ overflowX: "auto", mt: 1 }}>
        <Box
          component="table"
          sx={{
            borderCollapse: "separate",
            borderSpacing: "2px",
            width: "100%",
            tableLayout: "fixed",
            minWidth: 56 + hours.length * 44,
          }}
        >
          <Box component="thead">
            <Box component="tr">
              <Box component="th" sx={{ ...stickyLabelSx, minWidth: 48 }} />
              {hours.map((h) => (
                <Box
                  component="th"
                  key={h}
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: colors.textSecondary,
                    textAlign: "center",
                    minWidth: 40,
                    padding: "0 2px 4px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {hourLabel(h)}
                </Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {weekdays.map((wd) => (
              <Box component="tr" key={wd}>
                <Box component="th" sx={stickyLabelSx}>
                  {WEEKDAY_SHORT[wd]}
                </Box>
                {hours.map((h) => {
                  const cell = cellByKey.get(`${wd}-${h}`);
                  const available = cell && cell.hoursCapacity > 0;
                  if (!available) {
                    // sem capacidade = fora do horário de atendimento (não é ociosidade);
                    // ainda assim mostra o volume, para revelar atendimentos fora do horário.
                    const outSessions = cell?.sessions ?? 0;
                    return (
                      <Box
                        component="td"
                        key={h}
                        title={
                          outSessions > 0
                            ? `${WEEKDAY_SHORT[wd]} ${hourLabel(h)} — fora do horário de atendimento · ${outSessions} atendimento${outSessions === 1 ? "" : "s"}`
                            : undefined
                        }
                        sx={{
                          minWidth: 40,
                          height: 44,
                          textAlign: "center",
                          fontSize: 11,
                          color: colors.textSecondary,
                          background: "#f1f3f5",
                          borderRadius: "3px",
                          lineHeight: 1.15,
                        }}
                      >
                        {outSessions > 0 ? (
                          <>
                            <Box sx={{ fontSize: 12, fontWeight: 700 }}>—</Box>
                            <Box sx={{ fontSize: 9.5, fontWeight: 500 }}>
                              {outSessions} atend.
                            </Box>
                          </>
                        ) : (
                          "—"
                        )}
                      </Box>
                    );
                  }
                  const percent = cell!.occupancyPercent;
                  const sessions = cell!.sessions;
                  return (
                    <Box
                      component="td"
                      key={h}
                      title={`${WEEKDAY_SHORT[wd]} ${hourLabel(h)} — ${Math.round(
                        percent,
                      )}% · ${sessions} atendimento${sessions === 1 ? "" : "s"} (${formatHours(
                        cell!.hoursBooked,
                      )} de ${formatHours(cell!.hoursCapacity)})`}
                      sx={{
                        minWidth: 40,
                        height: 44,
                        textAlign: "center",
                        fontVariantNumeric: "tabular-nums",
                        color: heatTextColor(percent),
                        background: heatColor(percent),
                        borderRadius: "3px",
                        lineHeight: 1.15,
                      }}
                    >
                      <Box sx={{ fontSize: 12, fontWeight: 700 }}>
                        {`${Math.round(percent)}%`}
                      </Box>
                      <Box sx={{ fontSize: 9.5, fontWeight: 500, opacity: 0.85 }}>
                        {sessions > 0 ? `${sessions} atend.` : "—"}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Legenda: gradiente branco → teal (magnitude) */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5 }}>
        <Typography sx={{ fontSize: 11, color: colors.textSecondary }}>0%</Typography>
        <Box
          sx={{
            flex: "0 0 160px",
            height: 10,
            borderRadius: 5,
            border: "1px solid #e9ecef",
            background: `linear-gradient(to right, ${heatColor(0)}, ${heatColor(100)})`,
          }}
        />
        <Typography sx={{ fontSize: 11, color: colors.textSecondary }}>100%</Typography>
      </Box>

      <Typography sx={{ fontSize: 12, color: colors.textSecondary, mt: 1 }}>
        A cor/percentual é a ocupação da faixa (só horas com sala conhecida); o número
        abaixo é a quantidade de atendimentos da agenda naquele dia e hora.
      </Typography>
    </>
  );
};

// arredonda o topo do eixo Y para um valor "redondo" (1/2/5 × 10ⁿ)
const niceCeil = (value: number): number => {
  if (value <= 0) return 1;
  const pow = Math.pow(10, Math.floor(Math.log10(value)));
  const norm = value / pow;
  const nice = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10;
  return nice * pow;
};

/** Linha de horas agendadas por bucket (SVG desenhado à mão, sem lib de gráfico). */
const OccupancyTrendChart: React.FC<{ points: { label: string; hoursBooked: number }[] }> = ({
  points,
}) => {
  const width = 1000;
  const height = 280;
  const pad = { top: 26, right: 24, bottom: 52, left: 46 };
  const innerW = width - pad.left - pad.right;
  const innerH = height - pad.top - pad.bottom;

  const maxValue = points.reduce((m, p) => Math.max(m, p.hoursBooked), 0);
  const yMax = niceCeil(maxValue);
  const n = points.length;

  const xAt = (i: number) => (n === 1 ? pad.left + innerW / 2 : pad.left + (innerW * i) / (n - 1));
  const yAt = (v: number) => pad.top + innerH - (yMax === 0 ? 0 : (innerH * v) / yMax);
  const baseY = pad.top + innerH;

  const coords = points.map((p, i) => ({ x: xAt(i), y: yAt(p.hoursBooked) }));
  const linePath = coords.map((c, i) => `${i === 0 ? "M" : "L"} ${c.x} ${c.y}`).join(" ");
  const areaPath =
    coords.length > 0
      ? `${linePath} L ${coords[coords.length - 1].x} ${baseY} L ${coords[0].x} ${baseY} Z`
      : "";

  // 3 linhas de grade (0, meio, topo)
  const ticks = [0, yMax / 2, yMax];

  return (
    <Box sx={{ width: "100%", mt: 1 }}>
      <Box
        component="svg"
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="xMidYMid meet"
        sx={{ width: "100%", height: "auto", display: "block" }}
      >
        {ticks.map((t, i) => {
          const y = yAt(t);
          return (
            <g key={i}>
              <line x1={pad.left} y1={y} x2={pad.left + innerW} y2={y} stroke="#e9ecef" strokeWidth={1} />
              <text x={pad.left - 8} y={y + 3} textAnchor="end" fontSize={11} fill={colors.textSecondary}>
                {Math.round(t)}
              </text>
            </g>
          );
        })}

        {areaPath && (
          // área sutil sob a linha (teal 8%)
          <path d={areaPath} fill="rgba(3, 180, 198, 0.08)" stroke="none" />
        )}
        <path d={linePath} fill="none" stroke={colors.primary} strokeWidth={2} />

        {coords.map((c, i) => (
          <circle key={i} cx={c.x} cy={c.y} r={3} fill={colors.primary} />
        ))}

        {/* valor de horas em cada ponto (padrão uniforme, sem destaque só no pico) */}
        {points.map((p, i) =>
          p.hoursBooked > 0 ? (
            <text
              key={`v${i}`}
              x={xAt(i)}
              y={yAt(p.hoursBooked) - 8}
              textAnchor="middle"
              fontSize={9}
              fontWeight={600}
              fill={colors.textPrimary}
              style={{ fontVariantNumeric: "tabular-nums" }}
            >
              {Math.round(p.hoursBooked)}
            </text>
          ) : null,
        )}

        {/* rótulo dia a dia no eixo X (rotacionado para não sobrepor) */}
        {points.map((p, i) => (
          <text
            key={`l${i}`}
            x={xAt(i)}
            y={baseY + 14}
            textAnchor="end"
            fontSize={9}
            fill={colors.textSecondary}
            transform={`rotate(-45 ${xAt(i)} ${baseY + 14})`}
          >
            {p.label}
          </text>
        ))}
      </Box>
    </Box>
  );
};

const ReportOccupancy: React.FC = () => {
  const { navigateTo } = useRouter();
  const { user } = useAuth();

  const initialBounds = competenceBounds(currentCompetence());
  const [competence, setCompetence] = useState<string>(currentCompetence());
  const [fromDate, setFromDate] = useState<string>(initialBounds.from);
  const [toDate, setToDate] = useState<string>(initialBounds.to);
  const [data, setData] = useState<OccupancyResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    rooms: true,
    shifts: true,
    weekdays: true,
    heatmap: true,
    trend: true,
  });
  const toggleSection = (key: string) =>
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const defaultBounds = competenceBounds(currentCompetence());
  const hasFilterChanges =
    competence !== currentCompetence() ||
    fromDate !== defaultBounds.from ||
    toDate !== defaultBounds.to;

  const loadData = useCallback(async (from: string, to: string) => {
    if (!from || !to) return;
    setLoading(true);
    setError("");
    try {
      const response = await occupancyReportService.get(from, to);
      setData(response);
    } catch {
      setError("Não foi possível carregar a ocupação de salas. Tente novamente.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData(fromDate, toDate);
  }, [fromDate, toDate, loadData]);

  const handleCompetenceChange = (value: string) => {
    setCompetence(value);
    if (value) {
      const bounds = competenceBounds(value);
      setFromDate(bounds.from);
      setToDate(bounds.to);
    }
  };

  const clearFilters = () => {
    const competenceNow = currentCompetence();
    const bounds = competenceBounds(competenceNow);
    setCompetence(competenceNow);
    setFromDate(bounds.from);
    setToDate(bounds.to);
  };

  const handlePrint = () => {
    setTimeout(() => window.print(), 100);
  };

  const summary = data?.summary;
  const hasMovement = Boolean(
    summary && (summary.hoursBooked > 0 || summary.hoursWithoutRoom > 0),
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <style>{`
        @media print {
          .login-header, .login-footer-component, .no-print { display: none !important; }
          body { background: #fff !important; }
          main { margin-top: 0 !important; padding: 0 !important; background: #fff !important; }
          * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          @page { size: A4 landscape; margin: 10mm; }
          .occupancy-scroll { overflow: visible !important; }
          .occupancy-scroll > * { min-width: 0 !important; width: 100% !important; }
          thead { display: table-header-group; }
          .MuiCollapse-root { height: auto !important; visibility: visible !important; overflow: visible !important; }
          .MuiCollapse-wrapper, .MuiCollapse-wrapperInner { height: auto !important; }
        }
      `}</style>

      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={() => navigateTo("dashboard")}
      />

      <Box
        component="main"
        className="c4u-main"
      >
        <Container maxWidth={false} disableGutters>
          <Box
            className="c4u-page-header c4u-page-header--card"
          >
            <Box>
              <Typography variant="h4" className="c4u-page-header__title">
                Relatórios / Ocupação de Salas
              </Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                Horas agendadas contra a capacidade das salas no período, por sala, turno e dia da
                semana.
              </Typography>
            </Box>
            <Box className="no-print">
              <FaqButton />
            </Box>
          </Box>

          {/* Aviso: o relatório é uma foto do estado atual das alocações */}
          <Box className="c4u-info-box">
            <InfoOutlined className="c4u-info-box__icon" sx={{ fontSize: "1.1rem" }} />
            <Typography sx={{ fontSize: 12.5, color: colors.textSecondary, lineHeight: 1.45 }}>
              A sala de cada atendimento é a <strong>gravada no próprio agendamento</strong>;
              quando não há sala gravada, vale a <strong>sala padrão do profissional</strong>{" "}
              (alocação vigente). Atendimentos sem sala gravada — como os do histórico — seguem a
              alocação atual: remanejar um profissional de sala altera esses números,{" "}
              <strong>inclusive de períodos passados</strong>. Atendimentos com sala gravada não
              mudam.
            </Typography>
          </Box>

          {/* Filtros e ações */}
          <Paper className="no-print" elevation={0} sx={filterCardSx}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: "0.75rem",
                alignItems: { xs: "stretch", sm: "flex-start" },
                mb: 2,
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
                  gap: "0.75rem",
                  flex: 1,
                }}
              >
                <MonthSelect
                  label="Competência"
                  value={competence}
                  onChange={handleCompetenceChange}
                  sx={filterFieldSx}
                />
                <TextField
                  size="small"
                  type="date"
                  label="De"
                  value={fromDate}
                  onChange={(e) => {
                    setFromDate(e.target.value);
                    setCompetence("");
                  }}
                  fullWidth
                  sx={filterFieldSx}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  size="small"
                  type="date"
                  label="Até"
                  value={toDate}
                  onChange={(e) => {
                    setToDate(e.target.value);
                    setCompetence("");
                  }}
                  fullWidth
                  sx={filterFieldSx}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
              <IconButton
                onClick={clearFilters}
                disabled={!hasFilterChanges || loading}
                title="Limpar filtros"
                sx={{
                  ...clearFilterButtonSx,
                  flexShrink: 0,
                  alignSelf: { xs: "flex-end", sm: "auto" },
                }}
              >
                <FilterAltOff fontSize="small" />
              </IconButton>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                justifyContent: { sm: "flex-end" },
                flexWrap: { sm: "wrap" },
                gap: "0.5rem",
                // no celular a ação ocupa a largura toda (alvo de toque confortável)
                "& .MuiButton-root": {
                  flexShrink: 0,
                  minHeight: 44,
                },
              }}
            >
              <Button
                variant="contained"
                startIcon={<Print />}
                onClick={handlePrint}
                disabled={loading || !data}
                sx={actionButtonSx}
              >
                Imprimir
              </Button>
            </Box>
          </Paper>

          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress sx={{ color: colors.primary }} />
            </Box>
          )}

          {!loading && error && (
            <Box sx={{ ...sectionStyle, textAlign: "center", color: colors.error }}>{error}</Box>
          )}

          {!loading && !error && data && !hasMovement && (
            <Box sx={{ ...sectionStyle, textAlign: "center", color: colors.textSecondary }}>
              Nenhum atendimento agendado no período selecionado.
            </Box>
          )}

          {!loading && !error && data && summary && hasMovement && (
            <>
              {/* Aviso: a sala vem da alocação padrão enquanto a agenda estiver no Bubble */}
              <Box
                sx={{
                  ...sectionStyle,
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                  py: 1.25,
                  backgroundColor: colors.primaryLight,
                  border: "1px solid #cfeff3",
                }}
              >
                <InfoOutlined sx={{ fontSize: "1.1rem", color: colors.primary, mt: "1px" }} />
                <Typography sx={{ fontSize: 12, color: colors.textSecondary }}>
                  A sala de cada atendimento é uma sugestão — a sala padrão do profissional na data.
                  Remanejamentos pontuais não são registrados enquanto a agenda estiver no Bubble.
                  Por isso a ocupação é apresentada como uma banda, e não como um número único.
                </Typography>
              </Box>

              {/* Banda de ocupação: piso (sala conhecida) e teto (incluindo horas sem sala) */}
              <Box sx={{ ...sectionStyle, mb: 3 }}>
                <Typography sx={{ fontSize: 13, color: colors.textSecondary }}>
                  Ocupação do período
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "baseline",
                    flexWrap: "wrap",
                    gap: 1,
                    mt: 0.5,
                  }}
                >
                  <Typography sx={{ fontSize: 16, color: colors.textSecondary }}>entre</Typography>
                  <Typography
                    sx={{
                      fontSize: 30,
                      fontWeight: "bold",
                      color: occupancyColor(summary.occupancyAssignedPercent),
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {formatPercent(summary.occupancyAssignedPercent)}
                  </Typography>
                  <Typography sx={{ fontSize: 16, color: colors.textSecondary }}>e</Typography>
                  <Typography
                    sx={{
                      fontSize: 30,
                      fontWeight: "bold",
                      color: colors.textPrimary,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {formatPercent(summary.occupancyRealPercent)}
                  </Typography>
                </Box>
                <OccupancyRangeBar
                  floor={summary.occupancyAssignedPercent}
                  ceiling={summary.occupancyRealPercent}
                />
                <Typography sx={{ fontSize: 12, color: colors.textSecondary, mt: 1 }}>
                  O piso ({formatPercent(summary.occupancyAssignedPercent)}) são as{" "}
                  {formatHours(summary.hoursBooked)} com sala conhecida sobre{" "}
                  {formatHours(summary.hoursCapacity)} de capacidade. A diferença até o teto são as{" "}
                  {formatHours(summary.hoursWithoutRoom)} de profissionais sem sala definida — elas
                  aconteceram em alguma sala, mas o sistema não sabe qual.
                </Typography>
              </Box>

              {/* Alerta: atendimentos sobrepostos na mesma sala */}
              {summary.overlappingSessions > 0 && (
                <Box
                  sx={{
                    ...sectionStyle,
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    backgroundColor: "#fff3cd",
                    border: "1px solid #ffeaa7",
                  }}
                >
                  <WarningAmber sx={{ color: "#f59e0b" }} />
                  <Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#856404" }}>
                      {summary.overlappingSessions}{" "}
                      {summary.overlappingSessions === 1
                        ? "atendimento se sobrepõe"
                        : "atendimentos se sobrepõem"}{" "}
                      na mesma sala
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: "#856404" }}>
                      Duas pessoas não ocupam a mesma sala no mesmo horário — é sinal de sala
                      sugerida errada ou de remanejamento não registrado.
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Cards de resumo */}
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
                <Box sx={summaryCardSx}>
                  <Typography sx={{ fontSize: 13, color: colors.textSecondary }}>
                    Salas no pool
                  </Typography>
                  <Typography sx={{ fontSize: 22, fontWeight: "bold", color: colors.textPrimary }}>
                    {summary.roomsInPool}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: "#adb5bd" }}>
                    de {summary.roomsTotal} salas cadastradas
                  </Typography>
                </Box>
                <Box sx={summaryCardSx}>
                  <Typography sx={{ fontSize: 13, color: colors.textSecondary }}>
                    Horas indisponíveis
                  </Typography>
                  <Typography sx={{ fontSize: 22, fontWeight: "bold", color: "#6610f2" }}>
                    {formatHours(summary.hoursRented)}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: "#adb5bd" }}>
                    Fora do pool operacional (alugada, reunião etc.) — não é ociosidade
                  </Typography>
                </Box>
                <Box sx={summaryCardSx}>
                  <Typography sx={{ fontSize: 13, color: colors.textSecondary }}>
                    Horas sem sala definida
                  </Typography>
                  <Typography sx={{ fontSize: 22, fontWeight: "bold", color: colors.textPrimary }}>
                    {formatHours(summary.hoursWithoutRoom)}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: "#adb5bd" }}>
                    {summary.sessionsWithoutRoom}{" "}
                    {summary.sessionsWithoutRoom === 1 ? "atendimento" : "atendimentos"} de{" "}
                    {summary.professionalsWithoutRoom}{" "}
                    {summary.professionalsWithoutRoom === 1
                      ? "profissional"
                      : "profissionais"}{" "}
                    sem sala padrão
                  </Typography>
                </Box>
                <Box sx={summaryCardSx}>
                  <Typography sx={{ fontSize: 13, color: colors.textSecondary }}>
                    Horário útil considerado
                  </Typography>
                  <Typography sx={{ fontSize: 18, fontWeight: "bold", color: colors.textPrimary }}>
                    {summary.businessHours || "-"}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: "#adb5bd" }}>
                    Base do cálculo de capacidade
                  </Typography>
                </Box>
              </Box>

              {/* Ocupação por sala */}
              <Box sx={sectionStyle}>
                <SectionHeader
                  title={`Ocupação por sala (${data.rooms.length})`}
                  open={openSections.rooms}
                  onToggle={() => toggleSection("rooms")}
                  subtitle="Abaixo de 50% = ociosa (vermelho); 50% a 75% = atenção (amarelo); acima de 75% = saudável (verde). A capacidade já desconta os dias em que a sala está indisponível (alugada, reunião etc.)."
                />
                <Collapse in={openSections.rooms}>
                  {data.rooms.length === 0 ? (
                    <Typography sx={{ fontSize: 13, color: colors.textSecondary, py: 2 }}>
                      Nenhuma sala cadastrada no período.
                    </Typography>
                  ) : (
                    <Box className="occupancy-scroll" sx={{ overflowX: "auto" }}>
                      <Box sx={{ minWidth: 1040 }}>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns:
                              "minmax(180px, 1.4fr) minmax(150px, 1.3fr) 80px 110px 110px 100px 120px 70px",
                            gap: 1.5,
                            py: 0.75,
                            borderBottom: "2px solid #e9ecef",
                          }}
                        >
                          {[
                            "Sala",
                            "Especialidades",
                            "Sessões",
                            "Horas agend.",
                            "Capacidade",
                            "Sobrepostos",
                            "Ocupação",
                            "%",
                          ].map((header) => (
                            <Typography
                              key={header}
                              sx={{
                                fontSize: 12,
                                fontWeight: 600,
                                color: colors.textSecondary,
                                textAlign: header === "%" ? "right" : "left",
                              }}
                            >
                              {header}
                            </Typography>
                          ))}
                        </Box>
                        {data.rooms.map((room) => (
                          <Box
                            key={room.roomId}
                            sx={{
                              display: "grid",
                              gridTemplateColumns:
                                "minmax(180px, 1.4fr) minmax(150px, 1.3fr) 80px 110px 110px 100px 120px 70px",
                              gap: 1.5,
                              py: 0.75,
                              borderBottom: "1px solid #f1f3f5",
                              alignItems: "center",
                            }}
                          >
                            <Box sx={{ minWidth: 0, flex: "1 1 auto" }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 600, overflowWrap: "anywhere" }}>
                                  {room.roomName}
                                </Typography>
                                {rentalLabel(room.rentalWeekdays, room.fullyRented) && (
                                  <Chip
                                    label={rentalLabel(room.rentalWeekdays, room.fullyRented)}
                                    size="small"
                                    sx={{
                                      backgroundColor: "#6610f2",
                                      color: "white",
                                      fontWeight: 600,
                                      height: 20,
                                      fontSize: 11,
                                    }}
                                  />
                                )}
                              </Box>
                              {room.professionals.length > 0 && (
                                <Typography
                                  sx={{ fontSize: 11, color: "#adb5bd", overflowWrap: "anywhere" }}
                                >
                                  {room.professionals.join(", ")}
                                </Typography>
                              )}
                            </Box>
                            <Typography sx={{ fontSize: 12, color: colors.textSecondary, overflowWrap: "anywhere" }}>
                              {room.specialties.length > 0 ? room.specialties.join(", ") : "—"}
                            </Typography>
                            <Typography sx={{ fontSize: 13, fontVariantNumeric: "tabular-nums" }}>
                              {room.sessions}
                            </Typography>
                            <Typography sx={{ fontSize: 13, fontVariantNumeric: "tabular-nums" }}>
                              {formatHours(room.hoursBooked)}
                            </Typography>
                            <Typography sx={{ fontSize: 13, fontVariantNumeric: "tabular-nums" }}>
                              {room.fullyRented ? "—" : formatHours(room.hoursCapacity)}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: 13,
                                fontWeight: room.overlappingSessions > 0 ? 600 : 400,
                                fontVariantNumeric: "tabular-nums",
                                color:
                                  room.overlappingSessions > 0 ? "#dc3545" : colors.textSecondary,
                              }}
                              title={
                                room.overlappingSessions > 0
                                  ? "Atendimentos sobrepostos nesta sala — sala sugerida errada ou remanejamento não registrado"
                                  : undefined
                              }
                            >
                              {room.overlappingSessions > 0 ? room.overlappingSessions : "—"}
                            </Typography>
                            {room.fullyRented ? (
                              <Typography sx={{ fontSize: 12, color: "#adb5bd" }}>
                                Fora do pool
                              </Typography>
                            ) : (
                              <OccupancyBar percent={room.occupancyPercent} />
                            )}
                            <Typography
                              sx={{
                                fontSize: 13,
                                fontWeight: 600,
                                textAlign: "right",
                                fontVariantNumeric: "tabular-nums",
                                color: room.fullyRented
                                  ? "#adb5bd"
                                  : occupancyColor(room.occupancyPercent),
                              }}
                            >
                              {room.fullyRented ? "—" : formatPercent(room.occupancyPercent)}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Collapse>
              </Box>

              {/* Turno e dia da semana */}
              <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                <Box sx={{ ...sectionStyle, flex: "1 1 340px" }}>
                  <SectionHeader
                    title="Ocupação por turno"
                    open={openSections.shifts}
                    onToggle={() => toggleSection("shifts")}
                    subtitle="Horas agendadas sobre a capacidade do turno no período."
                  />
                  <Collapse in={openSections.shifts}>
                    {data.shifts.length === 0 ? (
                      <Typography sx={{ fontSize: 13, color: colors.textSecondary, py: 2 }}>
                        Sem dados de turno no período.
                      </Typography>
                    ) : (
                      data.shifts.map((item) => (
                        <Box
                          key={item.shift}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            py: 0.75,
                            borderBottom: "1px solid #f1f3f5",
                          }}
                        >
                          <Typography sx={{ fontSize: 13, flex: "0 0 90px" }}>
                            {label(item.shift, SHIFT_LABELS)}
                          </Typography>
                          <OccupancyBar percent={item.occupancyPercent} />
                          <Typography
                            sx={{
                              fontSize: 12,
                              color: colors.textSecondary,
                              width: 110,
                              textAlign: "right",
                              fontVariantNumeric: "tabular-nums",
                            }}
                          >
                            {formatHours(item.hoursBooked)} / {formatHours(item.hoursCapacity)}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: 600,
                              width: 58,
                              textAlign: "right",
                              color: occupancyColor(item.occupancyPercent),
                            }}
                          >
                            {formatPercent(item.occupancyPercent)}
                          </Typography>
                        </Box>
                      ))
                    )}
                  </Collapse>
                </Box>

                <Box sx={{ ...sectionStyle, flex: "1 1 340px" }}>
                  <SectionHeader
                    title="Ocupação por dia da semana"
                    open={openSections.weekdays}
                    onToggle={() => toggleSection("weekdays")}
                    subtitle="Dias de baixa ocupação são a folga real da agenda."
                  />
                  <Collapse in={openSections.weekdays}>
                    {data.weekdays.length === 0 ? (
                      <Typography sx={{ fontSize: 13, color: colors.textSecondary, py: 2 }}>
                        Sem dados por dia da semana no período.
                      </Typography>
                    ) : (
                      data.weekdays.map((item) => (
                        <Box
                          key={item.weekday}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            py: 0.75,
                            borderBottom: "1px solid #f1f3f5",
                          }}
                        >
                          <Typography sx={{ fontSize: 13, flex: "0 0 90px" }}>
                            {label(item.weekday, WEEKDAY_LABELS)}
                          </Typography>
                          <OccupancyBar percent={item.occupancyPercent} />
                          <Typography
                            sx={{
                              fontSize: 12,
                              color: colors.textSecondary,
                              width: 110,
                              textAlign: "right",
                              fontVariantNumeric: "tabular-nums",
                            }}
                          >
                            {formatHours(item.hoursBooked)} / {formatHours(item.hoursCapacity)}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: 600,
                              width: 58,
                              textAlign: "right",
                              color: occupancyColor(item.occupancyPercent),
                            }}
                          >
                            {formatPercent(item.occupancyPercent)}
                          </Typography>
                        </Box>
                      ))
                    )}
                  </Collapse>
                </Box>
              </Box>

              {/* Mapa de calor: dia da semana × hora */}
              <Box sx={{ ...sectionStyle, mt: 3 }}>
                <SectionHeader
                  title="Mapa de calor — dia da semana × hora"
                  open={openSections.heatmap}
                  onToggle={() => toggleSection("heatmap")}
                  subtitle="Quanto mais teal, maior a ocupação da faixa. Células cinza ('—') são horas sem sala conhecida ou indisponíveis."
                />
                <Collapse in={openSections.heatmap}>
                  {data.heatmap.length === 0 ? (
                    <Typography sx={{ fontSize: 13, color: colors.textSecondary, py: 2 }}>
                      Sem dados de faixa horária no período.
                    </Typography>
                  ) : (
                    <OccupancyHeatmap cells={data.heatmap} />
                  )}
                </Collapse>
              </Box>

              {/* Linha: volume de horas agendadas ao longo do período */}
              <Box sx={sectionStyle}>
                <SectionHeader
                  title="Horas agendadas no período"
                  open={openSections.trend}
                  onToggle={() => toggleSection("trend")}
                  subtitle={`${
                    data.trend.granularity === "semanal" ? "por semana" : "por dia"
                  } · inclui atendimentos com e sem sala.`}
                />
                <Collapse in={openSections.trend}>
                  {data.trend.points.length === 0 ||
                  data.trend.points.every((p) => p.hoursBooked === 0) ? (
                    <Typography sx={{ fontSize: 13, color: colors.textSecondary, py: 2 }}>
                      Sem atendimentos no período.
                    </Typography>
                  ) : (
                    <OccupancyTrendChart points={data.trend.points} />
                  )}
                </Collapse>
              </Box>
            </>
          )}
        </Container>
      </Box>

      <FooterInternal className="login-footer-component" />
    </Box>
  );
};

export default ReportOccupancy;
